import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Moon, ShoppingBag, Sun, Lock, X } from "lucide-react";
import { useShop } from "@/context/ShopContext";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Lookbook", to: "/lookbook" },
  { label: "Heritage", hash: "heritage" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const { count, setCartOpen, theme, toggleTheme } = useShop();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goHeritage = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById("heritage")?.scrollIntoView({ behavior: "smooth" }), 500);
    } else {
      document.getElementById("heritage")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-surface/85 backdrop-blur-xl border-b border-gold/20 shadow-[0_10px_40px_-20px_rgba(28,23,18,0.25)]" : "bg-bg/40 backdrop-blur-sm"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-brand-logo">
            <img
              src="/logo.jpg"
              alt="Sashti Jewels logo"
              className="h-10 w-10 lg:h-12 lg:w-12 rounded-full object-cover border border-gold/40 shadow-sm"
            />
            <span className="leading-none">
              <span className="block font-brand text-xl lg:text-2xl font-bold text-ink" style={{ letterSpacing: "0.02em" }}>Sashti</span>
              <span className="block font-cinzel text-[10px] font-semibold uppercase text-gold" style={{ letterSpacing: "0.42em" }}>
                Jewels
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {LINKS.map((l) =>
              l.hash ? (
                <a
                  key={l.label}
                  href={`/#${l.hash}`}
                  onClick={goHeritage}
                  className="font-cinzel text-[11px] font-semibold uppercase text-ink2 hover:text-gold transition-colors duration-300"
                  style={{ letterSpacing: "0.24em" }}
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.label}
                  to={l.to}
                  className="font-cinzel text-[11px] font-semibold uppercase text-ink2 hover:text-gold transition-colors duration-300"
                  style={{ letterSpacing: "0.24em" }}
                >
                  {l.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-3">
            <button
              onClick={toggleTheme}
              className="h-10 w-10 grid place-items-center text-ink2 hover:text-gold transition-colors"
              aria-label="Toggle theme"
              data-testid="nav-theme-toggle"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link
              to="/admin"
              className="hidden sm:grid h-10 w-10 place-items-center text-ink2 hover:text-gold transition-colors"
              aria-label="Admin portal"
              data-testid="nav-admin-button"
            >
              <Lock size={17} />
            </Link>
            <button
              onClick={() => setCartOpen(true)}
              className="relative h-10 w-10 grid place-items-center text-ink hover:text-gold transition-colors"
              aria-label="Open cart"
              data-testid="nav-cart-button"
            >
              <ShoppingBag size={19} />
              <AnimatePresence>
                {count > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 grid place-items-center rounded-full bg-gold text-[10px] font-semibold text-[#241A08] font-mono"
                    data-testid="cart-count-badge"
                  >
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
            <button
              onClick={() => setOpen(!open)}
              className="lg:hidden h-10 w-10 grid place-items-center text-ink"
              aria-label="Menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-gold/15 bg-surface/95 backdrop-blur-xl"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) =>
                l.hash ? (
                  <a key={l.label} href={`/#${l.hash}`} onClick={goHeritage} className="font-cinzel text-sm uppercase text-ink" style={{ letterSpacing: "0.24em" }}>
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="font-cinzel text-sm uppercase text-ink" style={{ letterSpacing: "0.24em" }}>
                    {l.label}
                  </Link>
                )
              )}
              <Link to="/admin" onClick={() => setOpen(false)} className="font-cinzel text-sm uppercase text-gold" style={{ letterSpacing: "0.24em" }}>
                Admin
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
